import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Car, ExternalLink } from 'lucide-react';

const VEHICLE_STATUS_CLASS = {
  disponible: 'bg-green-500 text-white',
  vendu: 'bg-red-500 text-white'
};
const getVehicleStatusClass = (status) => VEHICLE_STATUS_CLASS[status] || 'bg-amber-500 text-white';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VehiclesSaleModule({ authHeaders }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/vehicles`, { headers: authHeaders });
      setVehicles(res.data);
    } catch (err) {
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const disponibles = vehicles.filter(v => v.status === 'disponible');
  const vendus = vehicles.filter(v => v.status === 'vendu');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-[#DC2626] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="vehicles-sale-module">
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-4">
          <span className="text-sm text-gray-600"><span className="font-semibold text-green-600">{disponibles.length}</span> disponible{disponibles.length > 1 ? 's' : ''}</span>
          <span className="text-sm text-gray-600"><span className="font-semibold text-gray-400">{vendus.length}</span> vendu{vendus.length > 1 ? 's' : ''}</span>
        </div>
        <a href="/admin/dashboard" className="flex items-center gap-2 text-sm text-[#DC2626] hover:underline" data-testid="go-to-dashboard-link">
          Gérer dans le Dashboard <ExternalLink size={14} />
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vehicles.map(v => (
          <div key={v.id} className={`bg-white rounded-xl border overflow-hidden transition-shadow hover:shadow-md ${v.status === 'vendu' ? 'opacity-60' : ''}`}>
            <div className="relative h-40">
              <img
                src={v.images?.[0] || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400'}
                alt={v.title}
                className="w-full h-full object-cover"
              />
              <span className={`absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded ${getVehicleStatusClass(v.status)}`}>
                {v.status}
              </span>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-gray-900 mb-1">{v.title}</h3>
              <p className="text-[#DC2626] font-bold text-lg mb-2">{v.price?.toLocaleString('fr-FR')} €</p>
              <div className="flex gap-3 text-xs text-gray-500">
                <span>{v.year}</span>
                <span>{v.mileage?.toLocaleString('fr-FR')} km</span>
                <span>{v.fuel}</span>
                <span>{v.transmission}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!vehicles.length && (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Car size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-400">Aucun véhicule en vente</p>
          <a href="/admin/dashboard" className="text-sm text-[#DC2626] hover:underline mt-2 inline-block">Ajouter un véhicule dans le Dashboard</a>
        </div>
      )}
    </div>
  );
}
